<template>
  <div class="provide-battle-results">
    <div class="row">
      <Panel
        :variant="['transparent', 'stroked', 'stroked-gray', 'rounded']"
        class="w-100 p-4"
      >
        <HeraDummyList
          v-if="isLoading"
          class="position-relative pre-loader"
          position="center"
        />
        <template v-if="!isLoading">
          <div
            class="d-flex flex-column align-items-center justify-content-center"
          >
            <RoyaleScores
              v-model="resultsModel"
              class="scores-block flex-shrink-0 secondary-font royal-scores-new"
              edit-mode
              royale
              :input-max-length="5"
            />
          </div>
          <div class="d-flex justify-content-center mt-4">
            <Subheader
              class="text-center secondary-font"
              :size="0.875"
              :text="
                `${$$t('Please provide photos as proof', 'provide-proof')} (${
                  battle.attachmentRequired ? '*' : $$t('optional', 'optional')
                })`
              "
            />
          </div>
          <div class="row justify-content-center flex-shrink-1 mb-4">
            <div class="attachments-containter">
              <FormEditable
                :max-length="3"
                class="contenteditable"
                has-drop
                has-tags
                has-mentions
                :disable-autofocus="disableAutofocus"
                :grid="['Attachments']"
                :grid-models="[resultsModel.attachments]"
                @update="prepareResultsModelData"
                @submit="saveResults"
              />
            </div>
          </div>
        </template>
        <div class="footer d-flex justify-content-center mt-5">
          <Btn
            :icon="'chat-plus'"
            class="btn-toggle"
            :class="{ active: comment }"
            @click="toggleComment"
          />
          <HeraFormTextInput
            v-if="comment"
            v-model="resultsModel.text"
            class="my-2 mx-4 text-smaller-screen"
            :data-vv-as="$$t('Enter Your message', 'enter-message-placeholder')"
            :placeholder="
              $$t('Enter Your message', 'enter-message-placeholder')
            "
          />

          <Btn
            :label="$$t('Save results')"
            :disabled="isButtonDisabled"
            class="btn-accent btn-sm"
            @click="saveResults"
          />
        </div>
      </Panel>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import FormEditable from '@/components/form/contentEditable/';
import Subheader from '@/components/atoms/Subheader.vue';
import RoyaleScores from '@/components/tournaments/RoyaleScores.vue';
import Btn from '@/components/atoms/HeraFormBtn.vue';
import Panel from '@/components/atoms/Panel';
import HeraFormTextInput from '@/components/atoms/form/HeraFormTextInput';
import HeraDummyList from '@/components/common/HeraDummyList';
import { BATTLE_STATUSES } from '@/constants/battles';
import { translate } from '@/mixins/translate';
import { prop } from '@/utils/factories';

export default {
  name: 'ProvideBattleRoyaleResults',
  inject: ['$validator'],
  components: {
    Subheader,
    RoyaleScores,
    Btn,
    HeraFormTextInput,
    FormEditable,
    HeraDummyList,
    Panel,
  },
  mixins: [translate('_web_battle_results-modal')],
  props: {
    inline: prop(Boolean, false),
    disableAutofocus: prop(Boolean, false),
    login: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      resultsModel: {},
      isLoading: true,
      comment: false,
    };
  },

  computed: {
    ...mapState('current', {
      battleId: 'accountId',
    }),
    ...mapGetters({
      resultsByMatchId: 'resultsByMatchId',
      battle: 'dynamicBattleModel',
    }),
    results() {
      return this.resultsByMatchId(this.battle.id || this.battle._id) || {};
    },
    isButtonDisabled() {
      return !!(
        !this.resultsModel.rank ||
        this.resultsModel.kill_count == null ||
        this.errors.first('wrong-url') ||
        (this.battle.attachmentRequired &&
          !this.resultsModel.uri &&
          (!this.resultsModel.attachments ||
            !this.resultsModel.attachments.length))
      );
    },
    isBattleCompleted() {
      return this.battle.status === BATTLE_STATUSES.completed;
    },
  },
  watch: {
    results() {
      this.initResultsModel();
    },
  },
  created() {
    this.initResultsModel();
    this.isLoading = false;
  },
  methods: {
    ...mapActions(['setBattleResults', 'errorNotify']),
    initResultsModel() {
      if (this.$lodash.isEmpty(this.results)) {
        this.resultsModel = {
          battleID: this.battleId,
        };
        return;
      }
      let currentAccountResultsModel = {
        player_id: this.login,
        rank: null,
        kill_count: null,
      };
      this.resultsModel = {
        ...currentAccountResultsModel,
        battleID: this.battleId,
      };
    },
    saveResults() {
      this.setBattleResults(this.resultsModel)
        .then(() => {
          this.$emit('change', this.resultsModel);
        })
        .catch(this.errorNotify);
    },
    prepareResultsModelData(data) {
      if (typeof data === 'string') {
        //this is comment
        this.resultsModel = { ...this.resultsModel, text: data };
      }
      if (Array.isArray(data)) {
        //this is attachments
        this.resultsModel = Object.assign({}, this.resultsModel, {
          attachments: data.map(item => item.fileId),
        });
      }
    },
    toggleComment() {
      this.comment = !this.comment;
    },
  },
};
</script>

<style lang="scss">
.provide-battle-results {
  //contenteditable local fixes for new UI
  .contenteditable {
    margin-top: 0.7rem;
    &:focus-within {
      box-shadow: none;
    }
    .input-group {
      background-color: transparent;
      border: 1px solid var(--ph-input-borders);
      border-radius: 5px;
      height: 90px;
      width: 92px;
      overflow: hidden;

      &:focus-within {
        border: 1px solid $ph-accent;
      }

      * {
        background-color: $ph-input-bg;
      }
      .form-control-editable,
      .placeholder {
        padding: 0.5rem;
      }
      .placeholder {
        color: $ph-secondary-text;
        font-size: $ph-body-font-size;
        z-index: 3;
      }

      > div,
      > div button {
        height: 100%;
        width: 100%;
        margin: 0 !important;
        justify-content: center;
      }
    }
  }
  .attachments-preview {
    display: flex;
    width: auto;
    margin: 0 !important;
    padding: 0 !important;

    .attachments-item-media {
      margin: 0.675rem;
      width: 92px;
      height: 90px;
      border-radius: 8px;
      overflow: hidden;

      img,
      video {
        height: 100%;
        width: 100%;
        object-fit: cover;
      }
    }
  }

  .scores-block {
    border: none;
    font-size: 4rem;
    line-height: 4rem;
    input {
      height: 80px;
      width: 60px;
      background: var(--ph-card);
      border-radius: 8px;
      font-size: 40px;
      line-height: 75px;
    }
  }
  .battle-account {
    flex-basis: initial;
  }
  .footer {
    .btn {
      min-width: 9rem;

      &.btn-toggle {
        min-width: 3rem;
        padding: 0 !important;

        &.active svg {
          --fill: var(--ph-accent) !important;
        }
      }
    }
  }
}
.royal-scores-new {
  div {
    input {
      height: 80px;
      width: 60px;
      padding: 5px;
      text-align: center;
      background: var(--ph-card) !important;
      border-radius: 8px;
      font-size: 40px;
      line-height: 75px;
      outline: none;
    }
  }
}
@media (max-width: 767px) {
  .footer {
    flex-wrap: wrap;
  }
  .attachments-preview {
    justify-content: center;
    .attachments-item-media {
      margin-left: 10px !important;
      margin-right: 10px !important;
    }
  }
  .text-smaller-screen {
    max-width: 200px;
  }
}
</style>
